import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ReactSortable } from 'react-sortablejs';
import { useUnit } from 'hooks/units/index';
import stylesCourse from 'styles/Course.module.scss';
import styles from 'styles/Unit.module.scss';
import FormatContent from 'modules/Course/Unit/components/FormatContent';
import FormatContentArrow from 'modules/Course/Unit/components/FormatContentArrow';
import { parseHtml } from 'helpers';
import { Popover } from 'antd';
import useWindowSize from 'hooks/useWindowSize';
import stylespopover from 'styles/Calendar.module.scss';
import styled from '@emotion/styled';
import { UnitIdContext } from 'pages/course/[slug]/unit/[unitId]';

const DragAndDrop = ({ list, setList, question, test }) => {
  const unitSelected = useUnit();
  const QuesRef = useRef(null);
  const AnsRef = useRef(null);
  const [width, height] = useWindowSize();
  const containerRef = useRef(null);
  const isFullscreenMode = useContext(UnitIdContext);
  const [listHeight, setListHeight] = useState({
    listQus: [],
    listAns: [],
    commonHeight: [],
  });
  const [open, setOpen] = useState(false);
  const hide = () => {
    setOpen(false);
  };
  const handleOpenChange = (newOpen) => {
    setOpen(newOpen);
  };
  const listAnswer =
    question?.answers || unitSelected?.complete?.question?.answers;
  const formatCorrect = useMemo(
    () =>
      (question?.corrects || unitSelected?.complete?.question?.corrects)?.map(
        (answer) => ({
          content: answer,
        }),
      ),
    [question?.corrects || unitSelected?.complete?.question?.corrects],
  );

  const answeredFormat = useMemo(
    () =>
      test?.userQuestion?.result?.map((answer) => ({
        content: answer,
      })),
    [test?.userQuestion?.result],
  );

  useEffect(() => {
    if (test?.userQuestion?.result?.length) {
      setList(answeredFormat);
      sessionStorage.setItem('ordering', JSON.stringify(answeredFormat));
    } else {
      setList(formatCorrect);
      sessionStorage.setItem('ordering', JSON.stringify(formatCorrect));
    }
    return () => {
      sessionStorage.removeItem('ordering');
    };
  }, [formatCorrect, answeredFormat]);

  //this update height dynamically if list change
  useEffect(() => {
    if (listHeight?.commonHeight?.length === 0) {
      return null;
    } else {
      if (list?.length) {
        const newArray = list?.map((item, index) =>
          listHeight.listAns?.filter(
            (inItem, inIndex) => inItem?.content === item?.content,
          ),
        );
        const filterHeight = newArray?.map(
          (item, index) => item?.length && item[0],
        );
        filterHeight?.length &&
          setListHeight((preVal) => ({
            ...preVal,
            listAns: [...filterHeight],
          }));
      }
    }
  }, [list]);

  // get ques element height
  useEffect(() => {
    if (!QuesRef?.current) {
      return null;
    } else {
      const calculateHeights = () => {
        const newArray = Array.from(QuesRef.current.children).map(
          (item, index) =>
            item?.firstElementChild?.firstElementChild?.clientHeight,
        );
        newArray?.length &&
          setListHeight((preVal) => ({
            ...preVal,
            listQus: [...newArray],
          }));
      };
      const images = document.querySelectorAll('img');
      let loadedImagesCount = 0;

      const handleImageLoad = () => {
        loadedImagesCount += 1;
        if (loadedImagesCount === images.length) {
          calculateHeights();
        }
      };
      images.forEach((img) => {
        if (img.complete) {
          handleImageLoad();
        } else {
          img.addEventListener('load', handleImageLoad);
        }
      });

      // Cleanup event listeners
      return () => {
        images.forEach((img) => {
          img.removeEventListener('load', handleImageLoad);
        });
      };
    }
  }, [QuesRef?.current, width]);

  //  get  ans element height
  useEffect(() => {
    if (!AnsRef?.current) {
      return null;
    } else {
      if (list?.length) {
        const calculateHeights = () => {
          const newArray = Array.from(
            AnsRef?.current?.ref?.current.children,
          ).map((item, index) => ({
            content: list[index]?.content,
            height:
              item.firstElementChild?.firstElementChild?.firstElementChild
                ?.clientHeight,
            id: index,
          }));
          newArray?.length &&
            setListHeight((preVal) => ({
              ...preVal,
              listAns: [...newArray],
            }));
        };
        const images = document.querySelectorAll('img');
        let loadedImagesCount = 0;

        const handleImageLoad = () => {
          loadedImagesCount += 1;
          if (loadedImagesCount === images.length) {
            calculateHeights();
          }
        };
        images.forEach((img) => {
          if (img.complete) {
            handleImageLoad();
          } else {
            img.addEventListener('load', handleImageLoad);
          }
        });

        // Cleanup event listeners
        return () => {
          images.forEach((img) => {
            img.removeEventListener('load', handleImageLoad);
          });
        };
      }
    }
  }, [AnsRef?.current, width]);

  //  get common height from correct{qus, ans}
  useEffect(() => {
    if (listHeight.listAns.length === 0 && listHeight.listQus.length === 0) {
      return null;
    } else {
      const filterHeight = listHeight.listAns.map((item, index) =>
        listHeight.listQus[index] >= (item?.height ?? 0)
          ? listHeight.listQus[index]
          : item.height ?? 0,
      );
      filterHeight?.length &&
        setListHeight((preVal) => ({
          ...preVal,
          commonHeight: [...filterHeight],
        }));
    }
  }, [listHeight.listQus, listHeight.listAns]);

  useEffect(() => {}, [width]);
  const ContentExplanation = question?.data?.filter(
    (inItem) => inItem?.explanation !== '',
  );
  return (
    <div
      className={`${ContentExplanation?.length ? 'pr-2' : ''} ${
        styles.dragDropGroup
      }`}
    >
      <div ref={QuesRef}>
        <FormatContent
          list={listAnswer}
          commonHeight={listHeight.commonHeight}
        />
      </div>
      <div>
        <FormatContentArrow
          index={listAnswer.length}
          commonHeight={listHeight.commonHeight}
        />
      </div>
      <ReactSortable
        ref={AnsRef}
        list={list}
        swap
        animation={220}
        forceFallback
        easing="cubic-bezier(1, 0, 0, 1)"
        selectedClass={stylesCourse['item-section-selected']}
        setList={(newState) => {
          setList(newState);
          sessionStorage.setItem('ordering', JSON.stringify(newState));
        }}
      >
        {list?.length > 0 &&
          list?.map((item, index) => (
            <div>
              <div key={index}>
                <div
                  className={
                    `col position-relativecursor-pointer flexRow px-md-2 px-05 py-05 ${stylesCourse['item-section-normal']} user-select-none` +
                    ` ${stylesCourse['item-section-enabled']}` +
                    ` ${stylesCourse['animation-height']}`
                  }
                  style={{
                    maxWidth: '30rem',
                    height: listHeight.commonHeight[index],
                    '--labelcolor': '#c1c9d4',
                  }}
                >
                  <span className={`w-100 ${stylesCourse['item-unit-label']}`}>
                    <span
                      className={`drag-overflow-image ${stylesCourse['item-unit-title']}`}
                    >
                      {parseHtml(item?.content)}
                    </span>
                  </span>
                  {question?.data?.map((inItem) =>
                    inItem?.correct == item?.content ? (
                      <div
                        ref={containerRef}
                        className="position-absolute dragdrop-icon"
                      >
                        <Popover
                          {...(isFullscreenMode?.active
                            ? { getPopupContainer: () => containerRef.current }
                            : {})}
                          placement={isFullscreenMode?.active ? 'left' : 'top'}
                          content={
                            <div
                              className={`Popover-responsive ${stylespopover.Popover_responsive}`}
                              onClick={hide}
                            >
                              {parseHtml(inItem?.explanation)}
                            </div>
                          }
                          trigger="click"
                          open={open}
                          onOpenChange={handleOpenChange}
                        >
                          {inItem?.explanation?.length > 0 && (
                            <img
                              src={'/images/course/expand-view.png'}
                              style={{ height: 14, color: '#646871' }}
                            />
                          )}
                        </Popover>
                      </div>
                    ) : null,
                  )}
                </div>
              </div>
            </div>
          ))}
      </ReactSortable>
    </div>
  );
};

DragAndDrop.propTypes = {
  list: PropTypes.array.isRequired,
  setList: PropTypes.func.isRequired,
};

export default DragAndDrop;
